import { useCallback, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const AUDIO_ARCHIVE_BUCKET = "consultation-transcription-audio";
const AUDIO_ARCHIVE_TABLE = "consultation_transcription_audio_archives";
const AUDIO_ARCHIVE_DB = "consultation-transcription-audio-archive";
const AUDIO_ARCHIVE_VERSION = 1;
const SESSION_STORE = "archive_sessions";
const CHUNK_STORE = "archive_chunks";
const MEDIA_RECORDER_TIMESLICE_MS = 1000;
const SUPPORTED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

export type AudioArchiveState = "idle" | "recording" | "paused" | "pending_upload" | "uploading";

export interface AudioArchiveSessionContext {
  consultationTranscriptionId: string | null;
  clinicId: string;
  petId: string;
}

interface AudioArchiveSessionRecord extends AudioArchiveSessionContext {
  archiveSessionId: string;
  mimeType: string;
  startedAt: number;
  durationSeconds: number;
  status: "recording" | "pending_upload" | "uploading" | "uploaded" | "failed" | "canceled";
  sizeBytes: number;
  sequence: number;
}

interface AudioArchiveChunkRecord {
  archiveSessionId: string;
  sequence: number;
  blob: Blob;
  sizeBytes: number;
}

export interface FinalizedAudioArchiveSession {
  archiveSessionId: string;
  consultationTranscriptionId: string | null;
  clinicId: string;
  petId: string;
  startedAt: number;
  mimeType: string;
  durationSeconds: number;
  sizeBytes: number;
}

type UntypedSupabaseTable = {
  insert: (value: unknown) => {
    select: () => {
      single: () => Promise<{ data: Record<string, unknown> | null; error: unknown }>;
    };
  };
  update: (value: unknown) => {
    eq: (column: string, value: string) => Promise<{ data: Record<string, unknown> | null; error: unknown }>;
  };
};

function getUntypedTable(table: string): UntypedSupabaseTable {
  return (supabase.from as unknown as (relation: string) => UntypedSupabaseTable)(table);
}

function isIndexedDbAvailable() {
  return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
}

function openAudioArchiveDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(AUDIO_ARCHIVE_DB, AUDIO_ARCHIVE_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(SESSION_STORE)) {
        db.createObjectStore(SESSION_STORE, { keyPath: "archiveSessionId" });
      }

      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const store = db.createObjectStore(CHUNK_STORE, { keyPath: ["archiveSessionId", "sequence"] });
        store.createIndex("by_archive_session", "archiveSessionId", { unique: false });
      }
    };
  });
}

async function withDb<T>(handler: (db: IDBDatabase) => Promise<T>): Promise<T> {
  const db = await openAudioArchiveDb();

  try {
    return await handler(db);
  } finally {
    db.close();
  }
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

async function putSession(session: AudioArchiveSessionRecord): Promise<void> {
  await withDb(async (db) => {
    const transaction = db.transaction(SESSION_STORE, "readwrite");
    transaction.objectStore(SESSION_STORE).put(session);
    await transactionDone(transaction);
  });
}

async function putChunk(chunk: AudioArchiveChunkRecord): Promise<void> {
  await withDb(async (db) => {
    const transaction = db.transaction(CHUNK_STORE, "readwrite");
    transaction.objectStore(CHUNK_STORE).put(chunk);
    await transactionDone(transaction);
  });
}

async function getChunks(archiveSessionId: string): Promise<AudioArchiveChunkRecord[]> {
  return withDb(async (db) => {
    const transaction = db.transaction(CHUNK_STORE, "readonly");
    const index = transaction.objectStore(CHUNK_STORE).index("by_archive_session");
    const chunks = await requestToPromise(index.getAll(IDBKeyRange.only(archiveSessionId)));
    await transactionDone(transaction);

    return ((chunks as AudioArchiveChunkRecord[]) ?? []).sort((left, right) => left.sequence - right.sequence);
  });
}

async function purgeArchiveSession(archiveSessionId: string): Promise<void> {
  await withDb(async (db) => {
    const transaction = db.transaction([SESSION_STORE, CHUNK_STORE], "readwrite");
    transaction.objectStore(SESSION_STORE).delete(archiveSessionId);

    const chunkStore = transaction.objectStore(CHUNK_STORE);
    const index = chunkStore.index("by_archive_session");
    const keys = (await requestToPromise(index.getAllKeys(IDBKeyRange.only(archiveSessionId)))) as IDBValidKey[];

    for (const key of keys) {
      chunkStore.delete(key);
    }

    await transactionDone(transaction);
  });
}

async function purgeAllArchiveData(): Promise<void> {
  if (!isIndexedDbAvailable()) return;

  await withDb(async (db) => {
    const transaction = db.transaction([SESSION_STORE, CHUNK_STORE], "readwrite");
    transaction.objectStore(SESSION_STORE).clear();
    transaction.objectStore(CHUNK_STORE).clear();
    await transactionDone(transaction);
  });
}

export function selectSupportedAudioMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return "";
  }

  for (const mimeType of SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return "";
}

export function buildConsultationAudioArchivePath(session: {
  clinicId: string;
  petId: string;
  consultationTranscriptionId: string;
  startedAt: number;
  mimeType: string;
}) {
  const extension = session.mimeType.includes("ogg")
    ? "ogg"
    : session.mimeType.includes("mp4")
      ? "mp4"
      : "webm";

  return `${session.clinicId}/${session.petId}/${session.consultationTranscriptionId}/${session.startedAt}.${extension}`;
}

export function calculateChunkBytes(chunks: Array<{ sizeBytes: number }>) {
  return chunks.reduce((total, chunk) => total + chunk.sizeBytes, 0);
}

function createRecorder(stream: MediaStream, mimeType?: string) {
  try {
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);

    return {
      recorder,
      resolvedMimeType: recorder.mimeType || mimeType || "",
    };
  } catch (error) {
    console.warn("Audio archive recorder creation failed", error);
    return null;
  }
}

function tryStartRecorder(
  stream: MediaStream,
  mimeType: string,
  withTimeslice: boolean,
) {
  const recorderConfig = createRecorder(stream, mimeType || undefined);

  if (!recorderConfig) {
    return null;
  }

  const { recorder, resolvedMimeType } = recorderConfig;

  try {
    if (withTimeslice) {
      recorder.start(MEDIA_RECORDER_TIMESLICE_MS);
    } else {
      recorder.start();
    }

    return { recorder, resolvedMimeType };
  } catch (error) {
    if (withTimeslice) {
      console.warn("Audio archive recorder start with timeslice failed", error);
    } else {
      console.warn("Audio archive recorder start failed", error);
    }

    if (recorder.state === "recording") {
      return { recorder, resolvedMimeType };
    }

    return null;
  }
}

function startRecorderWithFallback(stream: MediaStream, preferredMimeType: string) {
  const attempts = [
    { mimeType: preferredMimeType, withTimeslice: true },
    { mimeType: preferredMimeType, withTimeslice: false },
    { mimeType: "", withTimeslice: true },
    { mimeType: "", withTimeslice: false },
  ];

  for (const attempt of attempts) {
    const started = tryStartRecorder(stream, attempt.mimeType, attempt.withTimeslice);
    if (started) {
      return started;
    }
  }

  return null;
}

function flushRecorderData(recorder: MediaRecorder | null) {
  if (!recorder || recorder.state !== "recording") return;

  try {
    recorder.requestData();
  } catch (error) {
    console.warn("Audio archive recorder flush failed", error);
  }
}

interface PendingUploadEntry {
  archiveSessionId: string;
  uploadPromise: Promise<void>;
  abortController: AbortController;
}

async function insertPendingArchiveMetadata(session: FinalizedAudioArchiveSession) {
  if (!session.consultationTranscriptionId) return null;

  try {
    const storagePath = buildConsultationAudioArchivePath({
      clinicId: session.clinicId,
      petId: session.petId,
      consultationTranscriptionId: session.consultationTranscriptionId,
      startedAt: session.startedAt,
      mimeType: session.mimeType,
    });

    const { data, error } = await getUntypedTable(AUDIO_ARCHIVE_TABLE)
      .insert({
        consultation_transcription_id: session.consultationTranscriptionId,
        clinic_id: session.clinicId,
        pet_id: session.petId,
        storage_bucket: AUDIO_ARCHIVE_BUCKET,
        storage_path: storagePath,
        mime_type: session.mimeType,
        size_bytes: session.sizeBytes,
        duration_seconds: session.durationSeconds,
        upload_status: "pending_upload",
      })
      .select()
      .single();

    if (error) {
      console.warn("Audio archive metadata insert failed", error);
      return null;
    }

    return { id: typeof data?.id === "string" ? data.id : undefined, storagePath };
  } catch (error) {
    console.warn("Audio archive metadata insert failed", error);
    return null;
  }
}

async function updateArchiveMetadata(id: string | null, changes: Record<string, unknown>) {
  if (!id) return;

  try {
    const { error } = await getUntypedTable(AUDIO_ARCHIVE_TABLE).update(changes).eq("id", id);
    if (error) {
      console.warn("Audio archive metadata update failed", error);
    }
  } catch (error) {
    console.warn("Audio archive metadata update failed", error);
  }
}

export const useConsultationAudioArchive = () => {
  const [archiveState, setArchiveState] = useState<AudioArchiveState>("idle");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const activeSessionRef = useRef<AudioArchiveSessionRecord | null>(null);
  const pendingUploadsRef = useRef<Map<string, PendingUploadEntry>>(new Map());

  const prepareNewSession = useCallback(async () => {
    for (const pendingUpload of pendingUploadsRef.current.values()) {
      pendingUpload.abortController.abort();
    }
    pendingUploadsRef.current.clear();

    mediaRecorderRef.current = null;
    activeSessionRef.current = null;
    setArchiveState("idle");

    if (!isIndexedDbAvailable()) return;

    try {
      await purgeAllArchiveData();
    } catch (error) {
      console.warn("Audio archive cleanup failed", error);
    }
  }, []);

  const start = useCallback(async (stream: MediaStream, sessionContext: AudioArchiveSessionContext) => {
    if (!isIndexedDbAvailable() || typeof MediaRecorder === "undefined") {
      return false;
    }

    const preferredMimeType = selectSupportedAudioMimeType();
    const recorderConfig = startRecorderWithFallback(stream, preferredMimeType);

    if (!recorderConfig) {
      setArchiveState("idle");
      return false;
    }

    const { recorder, resolvedMimeType } = recorderConfig;
    const archiveSessionId = crypto.randomUUID();
    const startedAt = Date.now();
    const nextSession: AudioArchiveSessionRecord = {
      archiveSessionId,
      consultationTranscriptionId: sessionContext.consultationTranscriptionId,
      clinicId: sessionContext.clinicId,
      petId: sessionContext.petId,
      mimeType: resolvedMimeType || preferredMimeType,
      startedAt,
      durationSeconds: 0,
      status: "recording",
      sizeBytes: 0,
      sequence: 0,
    };

    recorder.ondataavailable = (event) => {
      if (!event.data || event.data.size === 0 || !activeSessionRef.current) {
        return;
      }

      const currentSession = activeSessionRef.current;
      const nextSequence = currentSession.sequence + 1;

      currentSession.sequence = nextSequence;
      currentSession.sizeBytes += event.data.size;
      currentSession.durationSeconds = Math.max(
        1,
        Math.round((Date.now() - currentSession.startedAt) / 1000),
      );

      void Promise.all([
        putChunk({
          archiveSessionId: currentSession.archiveSessionId,
          sequence: nextSequence,
          blob: event.data,
          sizeBytes: event.data.size,
        }),
        putSession({ ...currentSession }),
      ]).catch((error) => {
        console.warn("Audio archive chunk persistence failed", error);
      });
    };

    activeSessionRef.current = nextSession;
    mediaRecorderRef.current = recorder;
    setArchiveState("recording");

    try {
      await putSession(nextSession);
      return true;
    } catch (error) {
      console.warn("Audio archive recorder start failed", error);
      mediaRecorderRef.current = null;
      activeSessionRef.current = null;
      setArchiveState("idle");

      try {
        await purgeArchiveSession(nextSession.archiveSessionId);
      } catch (purgeError) {
        console.warn("Audio archive cleanup after start failure failed", purgeError);
      }

      return false;
    }
  }, []);

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    const currentSession = activeSessionRef.current;

    if (!recorder || recorder.state !== "recording" || !currentSession) return;

    try {
      flushRecorderData(recorder);
      recorder.pause();
      currentSession.status = "recording";
      setArchiveState("paused");
      void putSession({ ...currentSession });
    } catch (error) {
      console.warn("Audio archive pause failed", error);
    }
  }, []);

  const resume = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    const currentSession = activeSessionRef.current;

    if (!recorder || recorder.state !== "paused" || !currentSession) return;

    try {
      recorder.resume();
      currentSession.status = "recording";
      setArchiveState("recording");
      void putSession({ ...currentSession });
    } catch (error) {
      console.warn("Audio archive resume failed", error);
    }
  }, []);

  const finalize = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    const currentSession = activeSessionRef.current;

    if (!currentSession) {
      setArchiveState("idle");
      return null;
    }

    const finishSession = async () => {
      const finalized: FinalizedAudioArchiveSession = {
        archiveSessionId: currentSession.archiveSessionId,
        consultationTranscriptionId: currentSession.consultationTranscriptionId,
        clinicId: currentSession.clinicId,
        petId: currentSession.petId,
        startedAt: currentSession.startedAt,
        mimeType: currentSession.mimeType,
        durationSeconds: Math.max(1, Math.round((Date.now() - currentSession.startedAt) / 1000)),
        sizeBytes: currentSession.sizeBytes,
      };

      currentSession.durationSeconds = finalized.durationSeconds;
      currentSession.status = "pending_upload";
      await putSession({ ...currentSession });
      setArchiveState("pending_upload");
      mediaRecorderRef.current = null;
      activeSessionRef.current = null;

      return finalized;
    };

    if (!recorder || recorder.state === "inactive") {
      return finishSession();
    }

    return new Promise<FinalizedAudioArchiveSession | null>((resolve) => {
      recorder.onstop = () => {
        void finishSession()
          .then(resolve)
          .catch((error) => {
            console.warn("Audio archive finalization failed", error);
            resolve(null);
          });
      };

      try {
        if (recorder.state === "recording") {
          flushRecorderData(recorder);
        }

        if (recorder.state === "paused") {
          recorder.resume();
        }
        recorder.stop();
      } catch (error) {
        console.warn("Audio archive stop failed", error);
        void finishSession()
          .then(resolve)
          .catch(() => resolve(null));
      }
    });
  }, []);

  const enqueueBackgroundUpload = useCallback(async (session: FinalizedAudioArchiveSession | null) => {
    console.log("enqueueBackgroundUpload", session);
    if (!session) {
      setArchiveState("idle");
      return;
    }

    const abortController = new AbortController();
    const uploadPromise = (async () => {
      let metadataId: string | null = null;

      try {
        setArchiveState("uploading");
        const metadataRecord = await insertPendingArchiveMetadata(session);
        metadataId = metadataRecord?.id ?? null;

        if (!session.consultationTranscriptionId || abortController.signal.aborted) {
          await updateArchiveMetadata(metadataId, {
            upload_status: "canceled",
            last_error: abortController.signal.aborted ? "aborted" : "missing_consultation_transcription_id",
          });
          return;
        }

        const chunks = await getChunks(session.archiveSessionId);
        const sizeBytes = calculateChunkBytes(chunks);
        const storagePath = metadataRecord?.storagePath ?? buildConsultationAudioArchivePath({
          clinicId: session.clinicId,
          petId: session.petId,
          consultationTranscriptionId: session.consultationTranscriptionId,
          startedAt: session.startedAt,
          mimeType: session.mimeType,
        });

        await updateArchiveMetadata(metadataId, {
          upload_status: "uploading",
          size_bytes: sizeBytes,
        });

        if (abortController.signal.aborted) {
          await updateArchiveMetadata(metadataId, {
            upload_status: "canceled",
            last_error: "aborted",
          });
          return;
        }

        const blob = new Blob(
          chunks.map((chunk) => chunk.blob),
          { type: session.mimeType || "audio/webm" },
        );

        const { error } = await supabase.storage
          .from(AUDIO_ARCHIVE_BUCKET)
          .upload(storagePath, blob, {
            upsert: false,
            contentType: session.mimeType || "audio/webm",
          });

        if (error) {
          throw error;
        }

        await updateArchiveMetadata(metadataId, {
          upload_status: "uploaded",
          uploaded_at: new Date().toISOString(),
          last_error: null,
          size_bytes: sizeBytes,
        });
      } catch (error) {
        console.warn("Audio archive upload failed", error);
        await updateArchiveMetadata(metadataId, {
          upload_status: abortController.signal.aborted ? "canceled" : "failed",
          last_error: error instanceof Error ? error.message : "upload_failed",
        });
      } finally {
        try {
          await purgeArchiveSession(session.archiveSessionId);
        } catch (error) {
          console.warn("Audio archive purge failed", error);
        }

        pendingUploadsRef.current.delete(session.archiveSessionId);
        setArchiveState("idle");
      }
    })();

    pendingUploadsRef.current.set(session.archiveSessionId, {
      archiveSessionId: session.archiveSessionId,
      uploadPromise,
      abortController,
    });

    await uploadPromise;
  }, []);

  const cancelAndPurge = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    const currentSession = activeSessionRef.current;

    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      } catch (error) {
        console.warn("Audio archive cancel failed", error);
      }
    }

    mediaRecorderRef.current = null;
    activeSessionRef.current = null;
    setArchiveState("idle");

    if (!currentSession) {
      await prepareNewSession();
      return;
    }

    try {
      await purgeArchiveSession(currentSession.archiveSessionId);
    } catch (error) {
      console.warn("Audio archive cancel purge failed", error);
    }
  }, [prepareNewSession]);

  return useMemo(() => ({
    archiveState,
    prepareNewSession,
    start,
    pause,
    resume,
    finalize,
    enqueueBackgroundUpload,
    cancelAndPurge,
  }), [
    archiveState,
    prepareNewSession,
    start,
    pause,
    resume,
    finalize,
    enqueueBackgroundUpload,
    cancelAndPurge,
  ]);
};
